import React, { useEffect, useRef, useState } from "react";
import { Box, Button, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { COLORS, MAX_KEYS } from "./constants";
import { commonInputProps, ghostButtonSx, primaryButtonSx, proseInputSx, secondaryButtonSx } from "./styles";
import { platformIcon, maskedInputProps, isMaskedValue } from "./utils";
import { presetFor } from "./platformPresets";
import { ConnectPanel } from "./ConnectPanel";
import { listPlatformNames } from "./destinationChanges";

// ─────────────────────────────────────────────────────────────────────────────
// OutputDestinationsPanel — the destinations editor (inside
// ManageDestinationsDialog)
//
//   header   title, one line of help, close (in its own spot, so it can never
//            sit on top of Save again)
//   list     every platform with its setup state at a glance: "Connected",
//            "2 destinations", "Not set up", plus a dot when it has unsaved edits
//   detail   the selected platform's settings
//   bar      "Unsaved changes on YouTube and Kick" + Discard / Save, or
//            "All changes saved" + Done
//
// Matches the "Manage destinations" board on the design canvas.
// ─────────────────────────────────────────────────────────────────────────────

// Index order is tabIndex everywhere (Onboarding.jsx links straight to these).
const PLATFORM_CARDS = [
  { id: "youtube",   kind: "youtube",   label: "YouTube",     tint: "rgba(239,68,68,0.16)" },
  { id: "facebook",  kind: "facebook",  label: "Facebook",    tint: "rgba(59,130,246,0.16)" },
  { id: "tiktok",    kind: "tiktok",    label: "TikTok",      tint: "rgba(255,255,255,0.10)" },
  { id: "linkedin",  kind: "linkedin",  label: "LinkedIn",    tint: "rgba(10,102,194,0.18)", preset: "linkedin" },
  { id: "x",         kind: "x",         label: "X",           tint: "rgba(255,255,255,0.10)", preset: "x" },
  { id: "instagram", kind: "instagram", label: "Instagram",   tint: "rgba(225,48,108,0.18)", preset: "instagram" },
  { id: "kick",      kind: "kick",      label: "Kick",        tint: "rgba(83,252,24,0.14)",  preset: "kick" },
  { id: "rtmp",      kind: "rtmp",      label: "Custom RTMP", tint: "rgba(43,189,175,0.16)" }
];

const SHARED_LIMIT_NOTE = "the limit is shared by LinkedIn, X, Instagram, Kick and Custom RTMP";

const noteSx  = { fontSize: 13, lineHeight: 1.55, color: COLORS.muted };
const labelSx = { display: "block", mb: 0.75, fontSize: 13, fontWeight: 600, color: COLORS.textSoft };
const monoSx  = { ...commonInputProps.sx, "& input": { fontFamily: COLORS.fontMono, fontSize: 13 } };
const editedSx = { "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(244,183,64,0.55) !important" } };

const removeBtnSx = {
  width: 44, height: 44, flex: "none", borderRadius: "12px", color: COLORS.red,
  border: `1px solid ${COLORS.border}`, "&:hover": { bgcolor: COLORS.dangerSoft, borderColor: "rgba(229,72,77,0.45)" }
};

// ── Small building blocks ────────────────────────────────────────────────────

function FieldLabel({ htmlFor, children, edited, hint }) {
  return (
    <Typography component="label" htmlFor={htmlFor} noWrap sx={{ ...labelSx, display: "block" }}>
      {children}
      {edited
        ? <Box component="span" sx={{ color: COLORS.amber, fontWeight: 600 }}> · edited</Box>
        : hint && <Box component="span" sx={{ color: COLORS.muted, fontWeight: 400 }}> · {hint}</Box>}
    </Typography>
  );
}

// A saved key comes back from the server masked. Said in the label rather than
// as helper text under the field, which made that one field taller than its
// neighbours and knocked the whole row out of line.
const keyHint = (value) => (isMaskedValue(value) ? "saved, type to replace" : null);

/** Stream key input: hidden by default with a show/hide eye, like a password. */
function SecretInput({ id, value, onChange, placeholder, edited }) {
  const [shown, setShown] = useState(false);
  const { onFocus } = maskedInputProps(value);
  return (
    <TextField
      {...commonInputProps}
      onFocus={onFocus}
      id={id}
      type={shown ? "text" : "password"}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      autoComplete="off"
      inputProps={{ spellCheck: false, autoComplete: "new-password" }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShown((v) => !v)}
              aria-label={shown ? "Hide stream key" : "Show stream key"}
              sx={{ color: COLORS.muted, "&:hover": { color: COLORS.text } }}>
              {shown ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
            </IconButton>
          </InputAdornment>
        )
      }}
      sx={{ ...monoSx, ...(edited ? editedSx : {}) }}
    />
  );
}

function Callout({ tone = "info", children }) {
  const warn = tone === "warn";
  return (
    <Box sx={{
      display: "flex", gap: 1.5, p: "14px 16px", borderRadius: "12px",
      bgcolor: warn ? "rgba(244,183,64,0.08)" : "rgba(255,255,255,0.03)",
      border: `1px solid ${warn ? "rgba(244,183,64,0.40)" : COLORS.border}`
    }}>
      {warn
        ? <WarningAmberIcon sx={{ color: COLORS.amber, fontSize: 20, mt: "1px", flex: "none" }} />
        : <InfoOutlinedIcon sx={{ color: COLORS.cyan, fontSize: 20, mt: "1px", flex: "none" }} />}
      <Typography sx={{ ...noteSx, m: 0, color: warn ? "#F2D9A6" : COLORS.muted }}>{children}</Typography>
    </Box>
  );
}

function OrDivider({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ flex: 1, height: "1px", bgcolor: COLORS.border }} />
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: COLORS.muted }}>{children}</Typography>
      <Box sx={{ flex: 1, height: "1px", bgcolor: COLORS.border }} />
    </Box>
  );
}

function AddRow({ onClick, disabled, children, note }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
      <Button startIcon={<AddIcon />} variant="outlined" onClick={onClick} disabled={disabled}
        sx={{ ...secondaryButtonSx, alignSelf: "center",
          "&.Mui-disabled": { color: COLORS.muted, borderColor: COLORS.border } }}>
        {children}
      </Button>
      {note && <Typography sx={{ fontSize: 12, color: COLORS.muted }}>{note}</Typography>}
    </Box>
  );
}

// ── The editor ───────────────────────────────────────────────────────────────

export function OutputDestinationsPanel({
  handleSubmit, tabIndex, setTabIndex,
  youtubeKeys, setYoutubeKeys,
  facebookKeys, setFacebookKeys,
  tiktokKey, setTiktokKey,
  rtmpDestinations, setRtmpDestinations,
  platformConnections, connectBusy,
  connectPlatform, disconnectPlatform, toggleConnection, saveMetadata,
  destLabels = {}, setDestLabels,
  saved = null, unsaved = [], onDiscard, onClose
}) {
  const tabRefs = useRef([]);
  const current = PLATFORM_CARDS[tabIndex] || PLATFORM_CARDS[0];

  // On a phone the list scrolls sideways; opening the editor on Kick (from
  // first-time setup, say) must not leave the selected platform off-screen.
  useEffect(() => {
    tabRefs.current[tabIndex]?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [tabIndex]);

  // Every URL-based platform and plain RTMP share ONE array, told apart by
  // `platform`. Rows keep their ABSOLUTE index so an edit never lands on a
  // different row than the one on screen.
  const ofPlatform = (id, rows = rtmpDestinations) =>
    (rows || [])
      .map((dest, index) => ({ dest, index }))
      .filter(({ dest }) => (dest.platform || "") === (id === "rtmp" ? "" : id));

  const savedRows = (id) => ofPlatform(id, saved?.rtmpDestinations).map(({ dest }) => dest);

  const setLabel = (platformId, value) =>
    setDestLabels?.((prev) => ({ ...prev, [platformId]: value }));

  // ── Status line for each platform in the list ─────────────────────────────
  const statusOf = (card) => {
    const n = (count) => `${count} destination${count === 1 ? "" : "s"}`;
    if (card.id === "youtube" || card.id === "facebook") {
      const conn = platformConnections?.[card.id];
      const keys = (card.id === "youtube" ? youtubeKeys : facebookKeys).filter(Boolean).length;
      if (conn) {
        const name = conn.selectedName || conn.channelTitle || conn.pageName || "";
        return { ok: !!conn.enabled, lead: conn.enabled ? "Connected" : "Connected · off",
          rest: [conn.enabled && name, keys ? `+${keys} key${keys === 1 ? "" : "s"}` : null].filter(Boolean).join(" · ") };
      }
      return keys ? { ok: true, lead: n(keys) } : { ok: false, lead: "Not set up" };
    }
    if (card.id === "tiktok") return tiktokKey ? { ok: true, lead: n(1) } : { ok: false, lead: "Not set up" };
    const count = ofPlatform(card.id).length;
    return count ? { ok: true, lead: n(count) } : { ok: false, lead: "Not set up" };
  };

  // Up/Down/Home/End move through the list, as a vertical tablist should.
  const onListKey = (e) => {
    const last = PLATFORM_CARDS.length - 1;
    const to = { ArrowDown: Math.min(last, tabIndex + 1), ArrowRight: Math.min(last, tabIndex + 1),
      ArrowUp: Math.max(0, tabIndex - 1), ArrowLeft: Math.max(0, tabIndex - 1), Home: 0, End: last }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    setTabIndex(to);
    tabRefs.current[to]?.focus();
  };

  const blockedReason = unsaved.length
    ? "Save or discard your changes first: signing in leaves this page."
    : "";

  // ── Row editors ───────────────────────────────────────────────────────────

  // Label + key, for YouTube/Facebook keys (slot ids YouTube1…) and TikTok.
  const keyRow = ({ slotId, defaultName, keyValue, savedKey, onKey, onRemove }) => (
    <Box key={slotId} sx={{
      display: "grid", gap: 1, alignItems: "end",
      gridTemplateColumns: { xs: "minmax(0,1fr) 44px", md: "180px minmax(0,1fr) 44px" }
    }}>
      <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
        <FieldLabel htmlFor={`${slotId}-label`}>Label</FieldLabel>
        <TextField {...commonInputProps} id={`${slotId}-label`} sx={proseInputSx}
          value={destLabels[slotId] || ""} placeholder={defaultName}
          inputProps={{ maxLength: 80 }}
          onChange={(e) => setLabel(slotId, e.target.value)} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <FieldLabel htmlFor={`${slotId}-key`} edited={!!saved && keyValue !== (savedKey || "")} hint={keyHint(keyValue)}>Stream key</FieldLabel>
        <SecretInput id={`${slotId}-key`} value={keyValue} onChange={onKey}
          placeholder={`Paste your ${defaultName.replace(/ \d+$/, "")} stream key`}
          edited={!!saved && keyValue !== (savedKey || "")} />
      </Box>
      <Tooltip title="Remove">
        <IconButton onClick={onRemove} aria-label={`Remove ${destLabels[slotId] || defaultName}`} sx={removeBtnSx}>
          <DeleteOutlineIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // Label + server URL + key, for every URL-based row.
  const urlRow = ({ dest, index }, n, platformLabel, savedDest) => {
    const update = (patch) => setRtmpDestinations((items) =>
      items.map((item, ci) => (ci === index ? { ...item, ...patch } : item)));
    const base = `row${index}`;
    const keyEdited = !!saved && (dest.key || "") !== (savedDest?.key || "");
    return (
      <Box key={base} sx={{
        display: "grid", gap: 1, alignItems: "end",
        gridTemplateColumns: { xs: "minmax(0,1fr) 44px", md: "150px minmax(0,1.2fr) minmax(0,1fr) 44px" }
      }}>
        <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
          <FieldLabel htmlFor={`${base}-name`}>Label</FieldLabel>
          <TextField {...commonInputProps} id={`${base}-name`} sx={proseInputSx}
            value={dest.name || ""} placeholder={`${platformLabel} ${n}`}
            inputProps={{ maxLength: 80 }}
            onChange={(e) => update({ name: e.target.value })} />
        </Box>
        <Box sx={{ minWidth: 0, gridColumn: { xs: "1 / -1", md: "auto" } }}>
          <FieldLabel htmlFor={`${base}-url`} edited={!!saved && (dest.url || "") !== (savedDest?.url || "")}>Server URL</FieldLabel>
          <TextField {...commonInputProps} id={`${base}-url`} value={dest.url || ""}
            placeholder="rtmps://…" inputProps={{ spellCheck: false }}
            onChange={(e) => update({ url: e.target.value })}
            sx={{ ...monoSx, ...(saved && (dest.url || "") !== (savedDest?.url || "") ? editedSx : {}) }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <FieldLabel htmlFor={`${base}-key`} edited={keyEdited} hint={keyHint(dest.key)}>Stream key</FieldLabel>
          <SecretInput id={`${base}-key`} value={dest.key || ""} placeholder="Stream key"
            onChange={(e) => update({ key: e.target.value })} edited={keyEdited} />
        </Box>
        <Tooltip title="Remove">
          <IconButton aria-label={`Remove ${dest.name || `${platformLabel} ${n}`}`} sx={removeBtnSx}
            onClick={() => setRtmpDestinations((items) => items.filter((_, ci) => ci !== index))}>
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // ── Detail for each platform ──────────────────────────────────────────────

  const renderOAuthPlatform = (id) => {
    const isYt = id === "youtube";
    const keys = isYt ? youtubeKeys : facebookKeys;
    const setKeys = isYt ? setYoutubeKeys : setFacebookKeys;
    const savedKeys = (isYt ? saved?.youtubeKeys : saved?.facebookKeys) || [];
    const name = isYt ? "YouTube" : "Facebook";
    return (
      <>
        <ConnectPanel
          platform={id} platformConnections={platformConnections} connectBusy={connectBusy}
          connectPlatform={connectPlatform} disconnectPlatform={disconnectPlatform}
          toggleConnection={toggleConnection} saveMetadata={saveMetadata}
          blockedReason={blockedReason}
        />
        <OrDivider>Or use a stream key instead</OrDivider>
        {keys.map((key, i) => keyRow({
          slotId: `${name}${i + 1}`, defaultName: `${name} ${i + 1}`,
          keyValue: key, savedKey: savedKeys[i],
          onKey: (e) => setKeys((list) => list.map((k, ci) => (ci === i ? e.target.value : k))),
          onRemove: () => setKeys((list) => list.filter((_, ci) => ci !== i))
        }))}
        <AddRow onClick={() => setKeys((list) => [...list, ""])} disabled={keys.length >= MAX_KEYS}
          note={`${keys.length} of ${MAX_KEYS} keys`}>
          Add {name} key
        </AddRow>
      </>
    );
  };

  const renderUrlPlatform = (card) => {
    const preset = card.preset ? presetFor(card.preset) : null;
    const rows = ofPlatform(card.id);
    const before = savedRows(card.id);
    const full = rtmpDestinations.length >= MAX_KEYS;
    const where = preset?.where || "Any service that accepts RTMP or RTMPS: paste its server URL and stream key.";
    return (
      <>
        <Callout>{where}</Callout>
        {preset?.verifyUrl && (
          <Callout tone="warn">
            We fill in {preset.label}'s usual address. Check it matches what {preset.label} shows you:
            a wrong URL only fails when you go live, not when you save.
          </Callout>
        )}
        {rows.length
          ? rows.map((row, n) => urlRow(row, n + 1, card.label, before[n]))
          : (
            <Box sx={{ p: "28px 20px", border: `1.5px dashed ${COLORS.borderStrong}`, borderRadius: "14px",
              textAlign: "center", fontSize: 14, color: COLORS.muted }}>
              No {card.label} destinations yet.
            </Box>
          )}
        <AddRow
          disabled={full}
          onClick={() => setRtmpDestinations((items) => [
            ...items, { url: preset?.defaultUrl || "", key: "", name: "", platform: preset ? preset.id : "" }
          ])}
          note={`${rtmpDestinations.length} of ${MAX_KEYS} used · ${SHARED_LIMIT_NOTE}`}>
          Add {card.label} destination
        </AddRow>
      </>
    );
  };

  const renderDetail = () => {
    if (current.id === "youtube" || current.id === "facebook") return renderOAuthPlatform(current.id);
    if (current.id === "tiktok") {
      return (
        <>
          <Callout>TikTok gives RTMP access to eligible accounts in LIVE Studio. Copy the stream key it shows you.</Callout>
          {keyRow({
            slotId: "TikTok", defaultName: "TikTok", keyValue: tiktokKey, savedKey: saved?.tiktokKey,
            onKey: (e) => setTiktokKey(e.target.value),
            onRemove: () => { setTiktokKey(""); setLabel("TikTok", ""); }
          })}
          <Typography sx={{ fontSize: 12, color: COLORS.muted }}>One TikTok destination per account.</Typography>
        </>
      );
    }
    return renderUrlPlatform(current);
  };

  const dirty = unsaved.length > 0;

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate
      sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, textAlign: "left" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2,
        px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 2.5, borderBottom: `1px solid ${COLORS.border}` }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography id="manage-destinations-title" component="h2"
            sx={{ fontFamily: COLORS.fontDisplay, fontWeight: 700, fontSize: 24, lineHeight: 1.15, color: COLORS.text }}>
            Destinations
          </Typography>
          <Typography sx={{ ...noteSx, mt: 0.5 }}>
            Where your broadcast goes. Connect an account, or paste a server URL and stream key.
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close destination settings"
          sx={{ flex: "none", color: COLORS.muted, "&:hover": { color: COLORS.text, bgcolor: "rgba(255,255,255,0.05)" } }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: { xs: "column", md: "row" } }}>
        {/* ── Platform list ── */}
        <Box role="tablist" aria-label="Platforms" aria-orientation="vertical" onKeyDown={onListKey}
          sx={{
            flex: { xs: "none", md: "0 0 272px" },
            display: "flex", flexDirection: { xs: "row", md: "column" }, gap: { xs: 1, md: "2px" },
            p: { xs: "12px 16px", md: "16px 12px" },
            borderRight: { md: `1px solid ${COLORS.border}` }, borderBottom: { xs: `1px solid ${COLORS.border}`, md: "none" },
            overflowX: { xs: "auto", md: "hidden" }, overflowY: { xs: "hidden", md: "auto" },
            scrollbarWidth: "thin"
          }}>
          {PLATFORM_CARDS.map((card, index) => {
            const on = index === tabIndex;
            const st = statusOf(card);
            const changed = unsaved.includes(card.id);
            return (
              <Box key={card.id} component="button" type="button" role="tab"
                id={`dest-tab-${card.id}`} aria-selected={on} aria-controls="dest-panel"
                tabIndex={on ? 0 : -1}
                ref={(el) => { tabRefs.current[index] = el; }}
                onClick={() => setTabIndex(index)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.5, flex: "none",
                  width: { md: "100%" }, p: { xs: "8px 12px 8px 8px", md: "10px 12px 10px 10px" },
                  borderRadius: "12px", cursor: "pointer", font: "inherit", textAlign: "left",
                  color: COLORS.text, border: "1px solid transparent",
                  bgcolor: on ? COLORS.raised : "transparent",
                  borderColor: on ? COLORS.borderStrong : { xs: COLORS.border, md: "transparent" },
                  "&:hover": { bgcolor: on ? COLORS.raised : "rgba(255,255,255,0.04)" },
                  "&:focus-visible": { outline: `3px solid ${COLORS.cyan}`, outlineOffset: 1 }
                }}>
                {platformIcon(card.kind, 36)}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 600, fontSize: 15, lineHeight: 1.3 }}>{card.label}</Typography>
                  <Typography noWrap sx={{ fontSize: 12, mt: 0.25, color: COLORS.muted, display: { xs: "none", md: "block" } }}>
                    <Box component="span" sx={{ color: st.ok ? COLORS.green : COLORS.muted, fontWeight: st.ok ? 600 : 400 }}>
                      {st.lead}
                    </Box>
                    {st.rest ? ` · ${st.rest}` : ""}
                  </Typography>
                </Box>
                {changed && (
                  <>
                    <Box component="span" title="Unsaved changes"
                      sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: COLORS.amber, flex: "none" }} />
                    <Box component="span" sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
                      unsaved changes
                    </Box>
                  </>
                )}
              </Box>
            );
          })}
        </Box>

        {/* ── Detail ── */}
        <Box id="dest-panel" role="tabpanel" aria-labelledby={`dest-tab-${current.id}`}
          sx={{
            flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain",
            p: { xs: 2, sm: 3 }, display: "flex", flexDirection: "column", gap: 2.5
          }}>
          {renderDetail()}
        </Box>
      </Box>

      {/* ── Save bar ── */}
      <Box role="status" sx={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5,
        px: { xs: 2, sm: 3 }, py: 2,
        pb: { xs: "calc(16px + env(safe-area-inset-bottom))", sm: 2 },
        bgcolor: dirty ? COLORS.raised : "transparent",
        borderTop: `1px solid ${dirty ? COLORS.borderStrong : COLORS.border}`
      }}>
        {dirty ? (
          <Typography sx={{ display: "inline-flex", alignItems: "center", gap: 1, fontSize: 14, fontWeight: 600, color: COLORS.amber,
            width: { xs: "100%", sm: "auto" } }}>
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: COLORS.amber }} />
            Unsaved changes on {listPlatformNames(unsaved)}
          </Typography>
        ) : (
          <Typography sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 14, color: COLORS.muted }}>
            <CheckIcon sx={{ fontSize: 18, color: COLORS.green }} />
            All changes saved
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {dirty ? (
          <>
            <Button onClick={onDiscard} sx={{ ...ghostButtonSx, height: 44 }}>Discard</Button>
            <Button type="submit" startIcon={<CheckIcon />} sx={{ ...primaryButtonSx, height: 44, px: 2.5 }}>
              Save changes
            </Button>
          </>
        ) : (
          <Button onClick={onClose} sx={{ ...ghostButtonSx, height: 44 }}>Done</Button>
        )}
      </Box>
    </Box>
  );
}
